import { Fragment, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, FileText, Download, Shield } from 'lucide-react'
import { Container, Breadcrumb } from '@/components/layout'
import {
  ProductGallery,
  ProductConfigurator,
  RelatedProducts,
} from '@/components/products'
import { useProduct } from '@/hooks/useProducts'
import { useCategory } from '@/hooks/useCategories'
import type { Certification, Document } from '@/types'

const BASE_URL = 'https://engimarinex.com'

function setMetaTag(name: string, content: string, isProperty = false) {
  const attribute = isProperty ? 'property' : 'name'
  let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, name)
    document.head.appendChild(element)
  }
  element.content = content
}

function setCanonicalUrl(url: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  link.href = url
}

export function ProductDetailPage() {
  const { category: categorySlug, product: productSlug } = useParams<{
    category: string
    product: string
  }>()

  const { data: product, isLoading: productLoading } = useProduct(productSlug)
  const { data: category } = useCategory(categorySlug)

  const breadcrumbItems = [
    { label: 'Products', href: '/products' },
    { label: category?.name || 'Category', href: `/products/${categorySlug}` },
    { label: product?.name || 'Product' },
  ]

  // Set SEO meta tags
  useEffect(() => {
    if (!product) return

    const title = product.meta_title || `${product.name} - EngiMarineX`
    const description = product.meta_description || product.description?.replace(/<[^>]*>/g, '').slice(0, 160) || ''
    const keywords = product.meta_keywords || ''
    const canonicalUrl = `${BASE_URL}/products/${categorySlug}/${productSlug}`
    const ogImage = product.og_image || product.images?.[0]?.image_url || `${BASE_URL}/images/og-default.jpg`

    document.title = title
    setMetaTag('title', title)
    setMetaTag('description', description)
    if (keywords) setMetaTag('keywords', keywords)
    setMetaTag('robots', 'index, follow')
    setCanonicalUrl(canonicalUrl)

    // Open Graph
    setMetaTag('og:type', 'product', true)
    setMetaTag('og:url', canonicalUrl, true)
    setMetaTag('og:title', title, true)
    setMetaTag('og:description', description, true)
    setMetaTag('og:image', ogImage, true)
    setMetaTag('og:site_name', 'EngiMarineX', true)

    // Twitter
    setMetaTag('twitter:card', 'summary_large_image')
    setMetaTag('twitter:title', title)
    setMetaTag('twitter:description', description)
    setMetaTag('twitter:image', ogImage)
  }, [product, categorySlug, productSlug])

  if (productLoading) {
    return (
      <section className="py-8">
        <Container>
          <div className="animate-pulse">
            <div className="h-4 bg-secondary-200 rounded w-48 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-4 aspect-square bg-secondary-200 rounded-xl" />
              <div className="lg:col-span-5 space-y-4">
                <div className="h-10 bg-secondary-200 rounded w-3/4" />
                <div className="h-4 bg-secondary-200 rounded w-1/4" />
                <div className="h-24 bg-secondary-200 rounded w-full" />
              </div>
              <div className="lg:col-span-3">
                <div className="h-96 bg-secondary-200 rounded-xl" />
              </div>
            </div>
          </div>
        </Container>
      </section>
    )
  }

  if (!product) {
    return (
      <section className="py-16 lg:py-24">
        <Container size="small">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-secondary-900 mb-4">
              Product Not Found
            </h1>
            <p className="text-secondary-600 mb-8">
              The product you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/products" className="btn-primary px-6 py-3">
              Browse Products
            </Link>
          </div>
        </Container>
      </section>
    )
  }

  const certifications = (product.certifications || []) as Certification[]
  const documents = (product as Product & { documents?: Document[] }).documents || []
  const features = product.features || []

  return (
    <>
      {/* Breadcrumb Header */}
      <section className="bg-white border-b border-secondary-100 py-4">
        <Container>
          <div className="flex items-center justify-between">
            <Breadcrumb items={breadcrumbItems} />
            <Link
              to={`/products/${categorySlug}`}
              className="hidden sm:flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {category?.name}
            </Link>
          </div>
        </Container>
      </section>

      {/* Main Product Content - 3 Column Layout */}
      <section className="py-8 lg:py-12 bg-white">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">

            {/* LEFT COLUMN - Gallery */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-4"
            >
              <div className="lg:sticky lg:top-8">
                <ProductGallery
                  images={product.images || []}
                  productName={product.name}
                />
              </div>
            </motion.div>

            {/* MIDDLE COLUMN - Product Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="space-y-6">
                {/* Product Name */}
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold text-primary-900 leading-tight">
                    {product.name}
                  </h1>
                  <p className="text-lg font-mono text-secondary-500 mt-2">
                    {product.code}
                  </p>
                </div>

                {/* Description */}
                {product.description && (
                  <div
                    className="prose prose-secondary max-w-none text-secondary-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                )}

                {/* Technical Features */}
                {features.length > 0 && (
                  <div className="pt-6 border-t border-secondary-100">
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                      Technical Features
                    </h3>
                    <ul className="space-y-3">
                      {features.map((feature) => (
                        <li key={feature.id} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0" />
                          <span className="text-secondary-600">
                            {feature.feature.split('\n').map((line, i, arr) => (
                              <Fragment key={i}>
                                {line}
                                {i < arr.length - 1 && <br />}
                              </Fragment>
                            ))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Certifications */}
                {certifications.length > 0 && (
                  <div className="pt-6 border-t border-secondary-100">
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                      Certifications & Approvals
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {certifications.map((cert) => (
                        <span
                          key={cert.id}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm border border-green-200"
                          title={cert.description || cert.name}
                        >
                          <Shield className="w-4 h-4" />
                          {cert.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                {documents.length > 0 && (
                  <div className="pt-6 border-t border-secondary-100">
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                      Documents & Downloads
                    </h3>
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <a
                          key={doc.id}
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors group"
                        >
                          <FileText className="w-5 h-5 text-secondary-400" />
                          <span className="flex-1 text-secondary-700 group-hover:text-secondary-900">
                            {doc.title}
                          </span>
                          <Download className="w-4 h-4 text-secondary-400 group-hover:text-primary-600" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* RIGHT COLUMN - Sticky Configurator */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-3"
            >
              <div className="lg:sticky lg:top-8">
                <ProductConfigurator product={product} />
              </div>
            </motion.div>
          </div>

          {/* Related Products */}
          {categorySlug && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-16 pt-12 border-t border-secondary-100"
            >
              <RelatedProducts
                currentProduct={product}
                categorySlug={categorySlug}
              />
            </motion.div>
          )}
        </Container>
      </section>
    </>
  )
}

interface Product {
  id: string
  code: string
  slug: string
  name: string
  description: string | null
  images?: { id: string; image_url: string; alt_text: string | null; is_primary: boolean; sort_order: number }[]
  features?: { id: string; feature: string; sort_order: number }[]
  filters?: { filter_type: string; filter_value: string }[]
  options?: { id: string; option_type: string; option_value: string; label: string; is_default: boolean }[]
  certifications?: Certification[]
  documents?: Document[]
  // SEO fields
  meta_title?: string | null
  meta_description?: string | null
  meta_keywords?: string | null
  og_image?: string | null
}
